/* Fiche produit Reliance : galerie, formats, quantité, résumé mobile et barre d’achat. */
(() => {
  /* Même rendu que le filtre Liquid `money_without_trailing_zeros`, au format de la boutique. */
  const formatMoney = (cents, format) => {
    const value = cents / 100;
    const pattern = format || '{{amount_with_comma_separator}} €';
    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      const decimal = key.includes('comma') ? ',' : '.';
      return Number.isInteger(value) ? String(value) : value.toFixed(2).replace('.', decimal);
    });
  };

  class RelPdpHero {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-rel-track]');
      this.slides = this.track ? Array.from(this.track.children) : [];
      this.dots = Array.from(root.querySelectorAll('[data-rel-go]'));
      this.legend = root.querySelector('[data-rel-legend]');
      this.form = root.querySelector('[data-rel-product-form] form');
      this.submit = root.querySelector('[data-rel-submit]');
      this.qtyInput = root.querySelector('[data-rel-qty-input]');
      this.radios = Array.from(root.querySelectorAll('input[type="radio"][name="id"]'));
      this.bar = root.querySelector('[data-rel-bar]');
      this.index = 0;

      this.initGallery();
      this.initVariants();
      this.initQuantity();
      this.initReadMore();
      this.initBar();
      this.initHeaderOffset();
      this.render();
    }

    /* ---------- Galerie */
    initGallery() {
      if (!this.track) return;
      this.root.querySelector('[data-rel-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.root.querySelector('[data-rel-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.relGo))));

      let startX = null;
      this.track.addEventListener('pointerdown', (event) => { startX = event.clientX; });
      this.track.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const delta = event.clientX - startX;
        startX = null;
        if (Math.abs(delta) > 40) this.goTo(this.index + (delta < 0 ? 1 : -1));
      });
      this.track.addEventListener('pointercancel', () => { startX = null; });
    }

    goTo(index) {
      const count = this.slides.length;
      if (!count) return;
      this.index = (index + count) % count;
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.slides.forEach((slide, i) => {
        if (i === this.index) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.legend) this.legend.textContent = this.slides[this.index].dataset.legend || '';
    }

    /* ---------- Formats */
    initVariants() {
      this.radios.forEach((radio) => radio.addEventListener('change', () => {
        const slide = radio.dataset.slide;
        if (slide !== '' && slide !== undefined) this.goTo(Number(slide));
        const url = new URL(window.location.href);
        url.searchParams.set('variant', radio.value);
        window.history.replaceState({}, '', url.toString());
        this.render();
      }));
      const checked = this.currentRadio();
      if (checked && checked.dataset.slide !== '') this.goTo(Number(checked.dataset.slide));
      else this.goTo(0);
    }

    currentRadio() {
      return this.radios.find((radio) => radio.checked) || null;
    }

    /* ---------- Quantité */
    initQuantity() {
      if (!this.qtyInput) return;
      this.root.querySelectorAll('[data-rel-qty]').forEach((button) => {
        button.addEventListener('click', () => {
          const next = this.quantity() + Number(button.dataset.relQty);
          this.qtyInput.value = Math.max(1, next);
          this.render();
        });
      });
      this.qtyInput.addEventListener('change', () => {
        this.qtyInput.value = this.quantity();
        this.render();
      });
    }

    quantity() {
      const value = parseInt(this.qtyInput?.value, 10);
      return Number.isInteger(value) && value > 0 ? value : 1;
    }

    /* ---------- Prix, disponibilité, barre mobile */
    render() {
      const radio = this.currentRadio();
      if (!radio) return;
      const available = radio.dataset.available === 'true';
      const total = formatMoney(Number(radio.dataset.priceCents) * this.quantity(), this.root.dataset.moneyFormat);
      this.root.querySelectorAll('[data-rel-total]').forEach((node) => { node.textContent = total; });
      const barLabel = this.root.querySelector('[data-rel-bar-label]');
      if (barLabel) barLabel.textContent = radio.dataset.label;

      if (this.submit) {
        this.submit.querySelector('[data-rel-submit-label]')?.classList.toggle('hidden', !available);
        this.submit.querySelector('[data-rel-soldout]')?.classList.toggle('hidden', available);
        if (available) this.submit.removeAttribute('aria-disabled');
        else this.submit.setAttribute('aria-disabled', 'true');
      }
      const barButton = this.bar?.querySelector('[data-rel-bar-submit]');
      if (barButton) barButton.toggleAttribute('disabled', !available);
    }

    /* ---------- Résumé replié sur mobile */
    initReadMore() {
      const box = this.root.querySelector('[data-rel-readmore]');
      if (!box) return;
      const toggle = box.querySelector('[data-rel-readmore-toggle]');
      const text = box.querySelector('p');
      const measure = () => {
        if (box.dataset.open === 'true') return;
        box.classList.add('is-clamped');
        toggle.hidden = text.scrollHeight <= text.clientHeight + 2;
        if (toggle.hidden) box.classList.remove('is-clamped');
      };
      toggle.addEventListener('click', () => {
        box.dataset.open = 'true';
        box.classList.remove('is-clamped');
        toggle.setAttribute('aria-expanded', 'true');
        toggle.hidden = true;
      });
      measure();
      window.addEventListener('resize', measure, { passive: true });
    }

    initBar() {
      if (!this.bar || !this.submit) return;
      this.bar.querySelector('[data-rel-bar-submit]')?.addEventListener('click', () => {
        if (this.submit.getAttribute('aria-disabled') === 'true') return;
        this.form.requestSubmit(this.submit);
      });
      let shown = false;
      /* La barre n’existe qu’en dessous de 900 px (display: none au-delà) */
      const wide = window.matchMedia('(min-width: 900px)');
      /* Signale la barre au reste de la page : le guide remonte au-dessus et la pastille cookies se masque (rel-guide.js) */
      const announce = () => {
        const visible = shown && !wide.matches;
        const html = document.documentElement;
        html.classList.toggle('rel-pdp-bar-visible', visible);
        html.style.setProperty('--rel-pdp-bar-height', visible ? `${this.bar.offsetHeight}px` : '0px');
        document.dispatchEvent(new CustomEvent('rel:pdp-bar', { detail: { visible } }));
      };
      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.bar.classList.toggle('is-visible', show);
        this.bar.setAttribute('aria-hidden', String(!show));
        this.bar.toggleAttribute('inert', !show);
        if (show === shown) return;
        shown = show;
        announce();
      });
      observer.observe(this.submit);
      wide.addEventListener('change', announce);
    }

    /* ---------- La galerie collante passe sous le header s’il est lui-même collant */
    initHeaderOffset() {
      const header = document.querySelector('.section-header');
      if (!header) return;
      const update = () => {
        const bottom = header.getBoundingClientRect().bottom;
        const offset = getComputedStyle(header).position === 'sticky' && bottom > 0 ? Math.round(bottom) : 0;
        this.root.style.setProperty('--rel-header-offset', `${offset}px`);
      };
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }
  }

  const init = () => {
    document.querySelectorAll('[data-rel-pdp-hero]').forEach((root) => {
      if (!root.relPdpHero) root.relPdpHero = new RelPdpHero(root);
    });
    /* Barre de senteurs défilante (mobile) : ramène la pastille active en vue, sans bouger la page. */
    document.querySelectorAll('[data-rel-scent-list]').forEach((list) => {
      const current = list.querySelector('[aria-current]');
      if (!current || list.scrollWidth <= list.clientWidth) return;
      const box = list.getBoundingClientRect();
      const pill = current.getBoundingClientRect();
      list.scrollLeft += pill.left - box.left - (box.width - pill.width) / 2;
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
